import { parseQuestionnaire } from '@/hooks/useQuestionnaire'
import type { MedRequest, ParsedQuestionnaire } from '@/types/data'
import usePatientOrderHistory from './api/med-requests/order-history'
import { useMemo } from 'react'
import { MetricPoint } from '@/components/common/metric-trend-chart'

export interface WeightAndHeightReading {
  orderId: string
  date: Date
  weightKg: number
  heightCm: number | null
}

export function isWeightLossOrder(medRequest?: MedRequest | null): boolean {
  return medRequest?.orderedItem?.category?.toLowerCase().includes('weight loss') ?? false
}

const answerText = (q: ParsedQuestionnaire): string => (Array.isArray(q.answer) ? q.answer.join(' ') : (q.answer ?? ''))

interface ParsedMeasurement {
  weightKg: number | null
  heightCm: number | null
  bmi: number | null
}

const MIN_WEIGHT_KG = 25
const MAX_WEIGHT_KG = 500
const MIN_HEIGHT_CM = 120
const MAX_HEIGHT_CM = 250

function parseMeasurementAnswer(raw: string): ParsedMeasurement {
  const text = raw.toLowerCase()
  const grab = (re: RegExp) => {
    const m = text.match(re)
    return m ? Number(m[1]) : null
  }

  let weightKg = grab(/weight\s*[=:]\s*(\d+(?:\.\d+)?)/)
  let heightCm = grab(/height\s*[=:]\s*(\d+(?:\.\d+)?)/)
  const bmi = grab(/bmi\s*[=:]\s*(\d+(?:\.\d+)?)/)

  if (weightKg && (weightKg < MIN_WEIGHT_KG || weightKg > MAX_WEIGHT_KG)) weightKg = null
  if (heightCm && (heightCm < MIN_HEIGHT_CM || heightCm > MAX_HEIGHT_CM)) heightCm = null

  return {
    weightKg,
    heightCm: heightCm === null ? null : Math.round(heightCm),
    bmi
  }
}

function getReadingDate(order: MedRequest, question: ParsedQuestionnaire | null): Date {
  // question.timestamp is a Unix timestamp in seconds; Date expects milliseconds.
  if (question?.timestamp != null) {
    return new Date(question.timestamp * 1000)
  }

  return new Date(order.creationDate ?? order.closingDate ?? order.prescriptionIssueDate ?? '')
}

interface OrderMeasurements {
  date: Date
  weightKg: number | null
  heightCm: number | null
}

/** Parse one order's consultation a single time into its date, current weight and height. */
function parseOrderMeasurements(order: MedRequest): OrderMeasurements {
  const questions =
    !order.questionaire || order.questionaire === 'null' ? [] : parseQuestionnaire(order.questionaire, {})

  const question: ParsedQuestionnaire | null =
    questions.find(q => {
      const title = q.title?.toLowerCase() ?? ''
      return title.includes('current') && (title.includes('bmi') || title.includes('weight'))
    }) ??
    questions.find(q => !(q.title?.toLowerCase() ?? '').includes('start')) ??
    null

  const measurement = question
    ? parseMeasurementAnswer(answerText(question))
    : { weightKg: null, heightCm: null, bmi: null }

  return {
    ...measurement,
    date: getReadingDate(order, question)
  }
}

/** Reconstruct a single weight reading from one order's consultation, or null. */
export function extractReadingFromOrder(order: MedRequest): WeightAndHeightReading | null {
  const { date, weightKg, heightCm } = parseOrderMeasurements(order)
  if (weightKg === null || Number.isNaN(date.getTime())) return null

  return { orderId: order.id, date, weightKg, heightCm }
}

export function buildWeightAndHeightReadings(
  current: MedRequest,
  previous: MedRequest[] = []
): WeightAndHeightReading[] {
  const seen = new Set<string>()
  const readings: WeightAndHeightReading[] = []

  for (const order of [current, ...previous]) {
    if (!order || seen.has(order.id)) continue
    seen.add(order.id)
    const reading = extractReadingFromOrder(order)
    if (reading) readings.push(reading)
  }

  return readings.sort((a, b) => a.date.getTime() - b.date.getTime())
}

/** Centimetre tolerance below which a height difference is treated as rounding noise. */
const HEIGHT_CHANGE_TOLERANCE_CM = 1

export interface HeightChangeFromReadings {
  changed: boolean
  currentCm: number | null
  previousCm: number | null
}

export function getHeightChangeFromReadings(
  weightAndHeightReadings: WeightAndHeightReading[]
): HeightChangeFromReadings {
  const currentCm = weightAndHeightReadings.at(-1)?.heightCm ?? null
  const previousCm = weightAndHeightReadings.at(-2)?.heightCm ?? null

  return {
    changed: currentCm !== null && previousCm !== null && Math.abs(currentCm - previousCm) > HEIGHT_CHANGE_TOLERANCE_CM,
    currentCm,
    previousCm
  }
}

export interface WeightAndHeightData {
  latest: WeightAndHeightReading | null
  heightChange: HeightChangeFromReadings
  chartData: MetricPoint[]
  isFetched: boolean
}

export function useWeightAndHeightData(medRequest: MedRequest): WeightAndHeightData {
  const { data: previousOrders, isFetched } = usePatientOrderHistory(medRequest.patient.id, !!medRequest.patient.id)

  const weightAndHeightReadings = useMemo(() => {
    const history = previousOrders ?? []
    const readings = buildWeightAndHeightReadings(medRequest, history)

    return {
      latest: readings.at(-1) ?? null,
      heightChange: getHeightChangeFromReadings(readings),
      chartData: readings.map(reading => ({
        date: reading.date,
        value: reading.weightKg
      })) satisfies MetricPoint[]
    }
  }, [medRequest, previousOrders])

  return {
    ...weightAndHeightReadings,
    isFetched
  }
}
